import EmployeeSingleIcon from '@assets/icons/single-employee.svg';
import MergeIcon from '@assets/icons/merged.svg';
import RemoveIcon from '@assets/icons/trash.svg';
import { createStyles } from '@/contexts/theme-context';
import { type FC } from 'react';
import { getMergeDisabledMessage } from '@/screens/order-inbounds/utils';
import { ICON_BUTTON_HIT_SLOP } from '@/components/navigation-header-content';
import { LIST_GAP_HORIZONTAL } from '@/styles';
import { ORDER_INBOUND_LIST_ITEM_TIME_WIDTH } from './order-inbounds-order-list';
import { OrderInboundAssignedEmployee } from '@/screens/order-inbounds/types';
import { OrderInboundsAssignedEmployee } from '@/screens/order-inbounds/order-inbounds-assigned-employee';
import { Tooltip } from '@usepepper/sprout-kit-ui';
import { TouchableOpacity, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';

interface OrderInboundsOrderListItemRightActionsProps {
  assignedEmployee?: OrderInboundAssignedEmployee;
  canMerge: boolean;
  isHovered: boolean;
  isSelected: boolean;
  onAssignEmployee: (employeeUUID: string) => Promise<void>;
  onAssignMenuOpenChange?: (isOpen: boolean) => void;
  onMerge: () => void;
  onRemove: () => void;
  onUnassignEmployee: () => void;
  renderFullWidth: boolean;
  restaurantName?: string | null;
  smartOrderUUID: string;
  supplierUUID: string;
}

export const OrderInboundsOrderListItemRightActions: FC<
  OrderInboundsOrderListItemRightActionsProps
> = ({
  assignedEmployee,
  canMerge,
  isHovered,
  isSelected,
  onAssignEmployee,
  onAssignMenuOpenChange,
  onMerge,
  onRemove,
  onUnassignEmployee,
  renderFullWidth,
  restaurantName,
  smartOrderUUID,
  supplierUUID,
}) => {
  const [styles, theme] = useStyles();

  const mergeDisabledMessage = getMergeDisabledMessage(restaurantName);

  const mergeButton = (
    <TouchableOpacity
      disabled={!canMerge}
      hitSlop={ICON_BUTTON_HIT_SLOP}
      onPress={onMerge}
      style={styles.action}
      testID="order-inbounds-merge-order-icon"
    >
      <MergeIcon
        fill={
          canMerge ? theme.sk.color.icon.primary : theme.sk.color.icon.disabled
        }
        height={20}
        width={20}
      />
    </TouchableOpacity>
  );

  return (
    <View
      style={[
        styles.actionContainer,
        renderFullWidth
          ? styles.actionContainerSmall
          : isHovered
            ? styles.actionContainerLarge
            : null,
        isSelected ? styles.actionContainerSelected : null,
        isHovered && !renderFullWidth ? styles.actionContainerHovered : null,
      ]}
    >
      <OrderInboundsAssignedEmployee
        assignedEmployee={assignedEmployee}
        avatarContainerStyle={styles.assignedEmployeeContainer}
        avatarSize="xsmall"
        onAssignEmployee={onAssignEmployee}
        onOpenChange={onAssignMenuOpenChange}
        onUnassignEmployee={onUnassignEmployee}
        renderUnassignedTrigger={(onPress) => (
          <TouchableOpacity
            hitSlop={ICON_BUTTON_HIT_SLOP}
            onPress={onPress}
            style={styles.action}
            testID="order-inbounds-order-list-item-assign-employee"
          >
            <EmployeeSingleIcon
              height={16}
              stroke={theme.sk.color.icon.primary}
              width={16}
            />
          </TouchableOpacity>
        )}
        restaurantName={restaurantName ?? null}
        smartOrderUUID={smartOrderUUID}
        supplierUUID={supplierUUID}
      />

      {canMerge ? (
        mergeButton
      ) : (
        <Tooltip placement="top">
          <Tooltip.Trigger>
            <View>{mergeButton}</View>
          </Tooltip.Trigger>
          <Tooltip.Content>
            <Typography
              color={theme.sk.color.text.ondark}
              variant="paragraph-md"
            >
              {mergeDisabledMessage}
            </Typography>
          </Tooltip.Content>
        </Tooltip>
      )}

      <TouchableOpacity
        hitSlop={ICON_BUTTON_HIT_SLOP}
        onPress={onRemove}
        style={styles.action}
        testID="remove-order-button"
      >
        <RemoveIcon
          fill={theme.colors.primary}
          height={16}
          strokeWidth={0.5}
          width={16}
        />
      </TouchableOpacity>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  action: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  actionContainer: {
    display: 'none',
    flexDirection: 'row',
    gap: LIST_GAP_HORIZONTAL,
    minHeight: 24,
  },
  actionContainerHovered: {
    backgroundColor: theme.sk.color.bg.secondary.hover,
  },
  actionContainerLarge: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    minWidth: ORDER_INBOUND_LIST_ITEM_TIME_WIDTH,
    paddingHorizontal: LIST_GAP_HORIZONTAL + 8,
    position: 'absolute',
    right: -16,
  },
  actionContainerSelected: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
  actionContainerSmall: {
    display: 'flex',
  },
  assignedEmployeeContainer: {
    top: 4,
  },
}));
